import React, { useState, useEffect, useRef } from 'react';
import { Input, Select, Tag, Spin, Empty, Row, Col } from 'antd';
import { SearchOutlined, CloseCircleOutlined, UserOutlined } from '@ant-design/icons';
import { trainingService } from '@iso/services';
import PersonDetail from './PersonDetail';

const { Option } = Select;

const getTypeColor = (type) => {
  const map = { staff: '#0B2A1B', visitor: '#1890ff', contractor: '#fa8c16' };
  return map[type] || '#8c8c8c';
};

const SearchPeople = ({ propertyId }) => {
  const [query, setQuery] = useState('');
  const [filterPersonType, setFilterPersonType] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!propertyId || query.length < 2) {
      if (query.length === 0) { setResults([]); setSearched(false); }
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(), 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, propertyId]);

  const doSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await trainingService.searchPeople(propertyId, query);
      if (res) setResults(res.people || []);
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = filterPersonType
    ? results.filter(r => r.person_type === filterPersonType)
    : results;

  if (selectedPerson) {
    return (
      <PersonDetail
        propertyId={propertyId}
        person={selectedPerson}
        onBack={() => setSelectedPerson(null)}
      />
    );
  }

  return (
    <div className="tab-content">
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} md={14}>
          <Input
            className="training-search-bar"
            size="large"
            prefix={<SearchOutlined />}
            suffix={query ? <CloseCircleOutlined onClick={() => { setQuery(''); setResults([]); setSearched(false); }} /> : null}
            placeholder="Search by name, email or phone..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </Col>
        <Col xs={24} md={10}>
          <Select
            style={{ width: '100%', height: 48 }}
            size="large"
            value={filterPersonType}
            onChange={setFilterPersonType}
            placeholder="All Person Types"
          >
            <Option value="">All Person Types</Option>
            <Option value="staff">Staff</Option>
            <Option value="visitor">Visitor</Option>
            <Option value="contractor">Contractor</Option>
          </Select>
        </Col>
      </Row>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      )}

      {!loading && searched && (
        <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 14 }}>
          Found {filteredResults.length} {filteredResults.length === 1 ? 'person' : 'people'}
        </div>
      )}

      {!loading && searched && filteredResults.length === 0 && (
        <Empty description="No people found matching your search" style={{ marginTop: 40 }} />
      )}

      {!loading && !searched && (
        <div style={{ textAlign: 'center', padding: 60, color: '#8c8c8c' }}>
          <UserOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
          <div style={{ fontSize: 16, fontWeight: 500 }}>Search for people</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>Enter a name, email, or phone number to search</div>
        </div>
      )}

      {!loading && filteredResults.map((person, idx) => (
        <div
          key={idx}
          className="person-card"
          role="button"
          tabIndex={0}
          onClick={() => setSelectedPerson(person)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedPerson(person);
            }
          }}
        >
          <div className="person-avatar" style={{ background: getTypeColor(person.person_type) }}>
            {(person.name || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div className="person-name">{person.name}</div>
            <div className="person-meta">
              {person.email && <span style={{ marginRight: 10 }}>{person.email}</span>}
              {person.phone && <span style={{ marginRight: 10 }}>{person.phone}</span>}
              {person.company && <span>{person.company}</span>}
            </div>
          </div>
          <Tag color={getTypeColor(person.person_type)} style={{ textTransform: 'capitalize' }}>
            {person.person_type}
          </Tag>
        </div>
      ))}
    </div>
  );
};

export default SearchPeople;
